import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, MessageSquare, Target, AlertCircle, RefreshCw, Hourglass, MicOff, Clock } from 'lucide-react';
import { format } from 'date-fns';
import {
  myConversationDetail,
  requestConversationSummary,
  type ConversationDetail,
  type SummaryStatus,
} from '@/lib/pulse/api';
import { toast } from 'sonner';

const STATUS_COPY: Record<SummaryStatus, { icon: typeof Hourglass; title: string; body: string }> = {
  ready: { icon: MessageSquare, title: '', body: '' },
  processing: {
    icon: Hourglass,
    title: 'Still being summarized',
    body: 'This conversation just ended. A short summary will appear here in a moment.',
  },
  no_content: {
    icon: MicOff,
    title: 'No conversation content',
    body: 'No spoken or typed turns were recorded, so there is nothing to summarize.',
  },
  too_short: {
    icon: MicOff,
    title: 'Too short to summarize',
    body: 'The conversation ended before there was enough back-and-forth to summarize.',
  },
  pending_retry: {
    icon: AlertCircle,
    title: 'Summary failed to generate',
    body: 'We can try again now — this happens occasionally when the AI service hiccups.',
  },
  not_ended: {
    icon: Clock,
    title: 'Conversation still open',
    body: 'A summary is produced once the conversation ends.',
  },
};

function SummaryStatusCard({
  status,
  conversationId,
  onRetried,
}: {
  status: SummaryStatus;
  conversationId: string;
  onRetried: () => void;
}) {
  const [retrying, setRetrying] = useState(false);
  const copy = STATUS_COPY[status];
  const Icon = copy.icon;

  async function handleRetry() {
    setRetrying(true);
    try {
      const { error } = await requestConversationSummary(conversationId);
      if (error) throw error;
      toast.success('Retry queued — refreshing in a moment.');
      setTimeout(onRetried, 2000);
    } catch (e) {
      toast.error((e as Error).message || 'Retry failed');
    } finally {
      setRetrying(false);
    }
  }

  return (
    <div className="rounded-md border border-border bg-muted/30 p-4 space-y-2">
      <div className="flex items-start gap-2">
        <Icon className="h-4 w-4 mt-0.5 text-muted-foreground" />
        <div className="space-y-1">
          <p className="text-sm font-medium">{copy.title}</p>
          <p className="text-xs text-muted-foreground">{copy.body}</p>
        </div>
      </div>
      {status === 'pending_retry' && (
        <Button size="sm" variant="outline" onClick={handleRetry} disabled={retrying} className="gap-2">
          <RefreshCw className={`h-3.5 w-3.5 ${retrying ? 'animate-spin' : ''}`} />
          {retrying ? 'Retrying…' : 'Retry summary'}
        </Button>
      )}
    </div>
  );
}

export function ConversationDetailDialog({
  conversationId,
  open,
  onOpenChange,
}: {
  conversationId: string | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const qc = useQueryClient();
  const queryKey = ['my_conversation_detail', conversationId];
  const { data, isLoading, error } = useQuery({
    enabled: open && !!conversationId,
    queryKey,
    queryFn: async () => {
      const { data, error } = await myConversationDetail(conversationId!);
      if (error) throw error;
      return data as ConversationDetail;
    },
  });

  const conv = data?.conversation;
  const summary = data?.summary;
  const meta = data?.summary_meta;
  const survey = data?.survey;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-primary" />
            Conversation report
          </DialogTitle>
          <DialogDescription>
            {conv
              ? `${format(new Date(conv.started_at), 'MMM d, yyyy • h:mm a')}${
                  conv.channel ? ` • ${conv.channel}` : ''
                }`
              : 'Loading conversation…'}
          </DialogDescription>
        </DialogHeader>

        {isLoading && (
          <div className="py-10 text-center text-muted-foreground">
            <Loader2 className="h-5 w-5 mx-auto animate-spin" />
          </div>
        )}

        {error && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
            <AlertCircle className="h-4 w-4 mt-0.5" />
            <span>{(error as Error).message}</span>
          </div>
        )}

        {data && (
          <div className="space-y-5">
            {/* Survey scores — always the last survey you completed */}
            {survey ? (
              <section className="space-y-2">
                <div className="flex items-center gap-2">
                  <Target className="h-4 w-4 text-primary" />
                  <h3 className="font-heading text-sm">Your scores · {survey.name}</h3>
                </div>
                {survey.north_vision && (
                  <p className="text-xs text-muted-foreground italic">
                    North vision: {survey.north_vision}
                  </p>
                )}
                <ul className="divide-y divide-border rounded-md border border-border overflow-hidden">
                  {survey.anchors.map((a) => {
                    const v = a.my_value;
                    const target = a.north_vision_target;
                    const gap = v != null && target != null ? v - target : null;
                    return (
                      <li key={a.anchor_id} className="p-3 space-y-1">
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-sm font-medium">{a.name}</span>
                          <div className="flex items-center gap-2">
                            {v == null ? (
                              <Badge variant="outline" className="text-xs">
                                Not answered
                              </Badge>
                            ) : (
                              <>
                                <span className="text-sm tabular-nums font-heading">
                                  {v}
                                  {target != null && (
                                    <span className="text-xs text-muted-foreground"> / target {target}</span>
                                  )}
                                </span>
                                {gap != null && (
                                  <Badge
                                    variant={gap >= 0 ? 'secondary' : 'destructive'}
                                    className="text-xs"
                                  >
                                    {gap > 0 ? '+' : ''}
                                    {gap}
                                  </Badge>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        {a.scale_labels && v != null && a.scale_labels[String(v)] && (
                          <p className="text-xs text-muted-foreground">
                            {a.scale_labels[String(v)]}
                          </p>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </section>
            ) : (
              <p className="text-sm text-muted-foreground">
                You haven't completed a survey yet — once you do, your scores will appear on every conversation report.
              </p>
            )}

            {/* Summary */}
            <section className="space-y-2">
              <h3 className="font-heading text-sm">Discussion summary</h3>
              {summary ? (
                <div className="rounded-md border border-border bg-muted/30 p-4">
                  <p className="text-sm whitespace-pre-line leading-relaxed">
                    {summary.key_points}
                  </p>
                  <p className="text-[11px] text-muted-foreground mt-3">
                    Summarized {format(new Date(summary.created_at), 'MMM d, yyyy • h:mm a')}
                  </p>
                </div>
              ) : meta ? (
                <SummaryStatusCard
                  status={meta.status}
                  conversationId={conversationId!}
                  onRetried={() => qc.invalidateQueries({ queryKey })}
                />
              ) : null}
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

